import { useCallback, useState } from 'react'
import { getArtifactAnnotations } from '@/lib/api'
import {
  mapAnnotationReport,
  shouldConfirmExport,
  type ExportGapSection,
} from '@/lib/exportGaps'

interface UseExportGapCheckOptions {
  artifactId: string | undefined
  /** The outline the page already holds — used to order and label the report. */
  outlineSections: ReadonlyArray<{ sectionKey: string; title: string }>
  /** Label for `__document__` and any key the outline no longer holds. */
  fallbackTitle: string
  /**
   * Where "Review gaps" sends the author. `sectionKey` is the first affected
   * section that resolved against the outline, or null when none did — pages
   * with a single editor route ignore it.
   */
  onReviewGaps: (sectionKey: string | null) => void
}

interface PendingExport {
  run: () => void | Promise<void>
  sections: ExportGapSection[]
  attachmentCount: number
  firstSectionKey: string | null
}

export interface ExportGapDialogProps {
  open: boolean
  sections: ExportGapSection[]
  attachmentCount: number
  onExportAnyway: () => void
  onReviewGaps: () => void
  onOpenChange: (open: boolean) => void
}

/**
 * Pre-export gap check for the artifact overview pages (spec 19).
 *
 * Wraps an export in a report call: if the draft still holds evidence gaps — or
 * a malformed token that export cannot strip — the export is held and the
 * confirm dialog takes over. Otherwise it runs untouched.
 *
 * The section editor does NOT use this: it already has its content in memory and
 * scans locally via `scanContentForGaps`.
 */
export function useExportGapCheck({
  artifactId,
  outlineSections,
  fallbackTitle,
  onReviewGaps,
}: UseExportGapCheckOptions) {
  const [pending, setPending] = useState<PendingExport | null>(null)

  const runWithGapCheck = useCallback(
    async (run: () => void | Promise<void>) => {
      if (artifactId) {
        try {
          const report = await getArtifactAnnotations(artifactId)
          if (shouldConfirmExport(report)) {
            setPending({ run, ...mapAnnotationReport(report, outlineSections, fallbackTitle) })
            return
          }
        } catch {
          // Never block an export on the report. The server strips annotations
          // whatever happens here, so a failed check costs the author
          // information, not a correct document — and a toast would land beside
          // the export's own success toast and read as a failed export.
        }
      }
      await run()
    },
    [artifactId, outlineSections, fallbackTitle],
  )

  const exportGapDialogProps: ExportGapDialogProps = {
    open: pending !== null,
    sections: pending?.sections ?? [],
    attachmentCount: pending?.attachmentCount ?? 0,
    onOpenChange: (open) => {
      if (!open) setPending(null)
    },
    onExportAnyway: () => {
      const run = pending?.run
      setPending(null)
      void run?.()
    },
    onReviewGaps: () => {
      const sectionKey = pending?.firstSectionKey ?? null
      setPending(null)
      onReviewGaps(sectionKey)
    },
  }

  return { runWithGapCheck, exportGapDialogProps }
}
